import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdministratorCard from './AdministratorCard';
import CardholderCard from './CardholderCard';
import CardHeader from 'react/shared/components/true_link/lab/form/wizard/CardHeader';
import CardText from 'react/shared/components/true_link/lab/form/wizard/CardText';
import NextPageButton from 'react/shared/components/true_link/lab/form/wizard/NextPageButton';
import PreviousPageButton from 'react/shared/components/true_link/lab/form/wizard/PreviousPageButton';
import TrueLinkAccordion from 'react/shared/components/true_link/main/TrueLinkAccordion';
import theme, { mediumBreakpointMediaQuery } from 'react/shared/theme/Theme';
import PALETTE from 'react/shared/theme/palette';
import { possessiverize } from 'react/shared/utils/Strings';
import WizardCard from 'react/signups/card/common/WizardCard';
import { goBack } from 'react/signups/card/routing/navigator';
import trusteeDataShape from 'react/signups/card/shapes/TrusteeData';

const useStyles = makeStyles(() => ({
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(5),
  },
  accordionContainer: {
    textAlign: 'center',
  },
  accordionHeader: {
    color: PALETTE.emerald,
  },
  accordions: {
    padding: '20px 0',
  },
  nextPageAccordionButton: {
    textAlign: 'center',
  },
  mobileMargin: {
    marginBottom: '70px',
  },
  previousPageButton: {
    marginLeft: theme.spacing(4),
  },
  nextPageButton: {
    margin: '2rem auto',
    width: '400px',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
      width: '62%',
    },
    [theme.breakpoints.up('md')]: {
      display: 'block',
      width: '40%',
    },
  },
  sntNextBtn: {
    marginTop: '20px',
  },
  captionCard: {
    margin: '15px 0 50px',
    padding: '30px 48px',

    [theme.breakpoints.down('md')]: {
      paddingTop: '0',
      textAlign: 'center',
    },
  },
}));

const accordionContent = [
  {
    header:
      'Can the True Link Visa® Protection Card be used when a person has benefits and/or a special needs trust?',
    key: 'prePaidCard-1',
    content: (
      <p>
        Yes, the Social Security Administration (SSA)1 has provided guidelines for using the True
        Link Visa® Card to make disbursements from Special Needs Trusts (SNT) in a way that helps
        prevent jeopardizing benefits eligibility.
      </p>
    ),
    footnote: (
      <>
        <sup>1</sup>Not associated with or endorsed by the Social Security Administration or any
        other government agency
      </>
    ),
  },
  {
    header: 'Whose name should be on the True Link Visa Card?',
    key: 'prePaidCard-2',
    content: (
      <div>
        <p>
          The beneficiary’s name. The SSA requires that the account and True Link Visa Card be owned
          and managed by the trustee, but True Link is designed so that the Visa Card reflects the
          beneficiary's name.
        </p>
        <p>
          We will also need information about the trustee, as we are required to verify the identity
          of both the True Link Visa Card administrator and cardholder in accordance with federal
          law. We do not run credit checks nor share this information with credit bureaus.
        </p>
      </div>
    ),
  },
  {
    header: 'Are the funds counted as income?',
    key: 'prePaidCard-3',
    content: (
      <p>
        When used correctly, the Visa Card is not counted as income or a resource. The SSA deems the
        Visa Card owned by the trust for the benefit of the beneficiary (regardless of the name
        printed on the front of the card) so that distributions to the Visa Card are not considered
        income.'
      </p>
    ),
    footnote:
      "Note: If not set up properly, the use of the Visa card could be considered income to the beneficiary — if it's food or shelter, it's still food or shelter if you pay for it with the Visa Card.",
  },
  {
    header: 'Who sets up the spending rules on the Visa Card?',
    key: 'prePaidCard-4',
    content: (
      <>
        <p>
          The Administrator sets the spending rules. The True Link Protection Visa Prepaid Card is
          an administrator-managed prepaid Visa Card with spending controls that allow a trustee to
          exercise discretion over where and how it is used. This means that the trustee is the
          administrator who manages how and where the Visa Card works by setting spending rules in
          True Link’s online platform: “Spending Monitor“.
        </p>
        <p>
          The Visa Card can be set up to allow only approved purchases and block all purchases that
          are not authorized by the trustee. With these controls in place, trustees have an
          auditable way to manage trust disbursements in alignment with the POMS Guidelines.
        </p>
      </>
    ),
  },
];

export default function Thanks({
  cardholderFirstName,
  moduleComplete,
  setThanksData,
  trusteeData,
}) {
  const handleContinue = () => setThanksData(true);
  const classes = useStyles();
  const desktop = useMediaQuery(mediumBreakpointMediaQuery);
  const navigate = useNavigate();

  const handleBack = () => {
    goBack(navigate);
  };

  const [expanded, setExpanded] = useState('prePaidCard-1');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : '');
  };

  return (
    <>
      {trusteeData.trustee === 'yes' ? (
        <>
          <WizardCard>
            <div className={classes.accordionContainer}>
              <CardHeader className={classes.accordionHeader}>
                The True Link Visa® Prepaid Card is made to work with Special Needs Trusts
              </CardHeader>
              <Typography variant="body1">Commonly asked questions</Typography>

              <div className={classes.accordions}>
                {accordionContent.map((item) => (
                  <TrueLinkAccordion
                    {...{
                      header: item.header,
                      icon: item.icon,
                      id: item.key,
                    }}
                    expanded={expanded}
                    handleChange={handleChange(item.key)}
                    key={item.key}
                  >
                    {item.content}
                    {item.footnote && <i>{item.footnote}</i>}
                    {item.link && (
                      <a href={item.linkUrl} rel="noreferrer" target="_blank">
                        {item.link}
                      </a>
                    )}
                  </TrueLinkAccordion>
                ))}
              </div>
              <NextPageButton
                className={classes.sntNextBtn}
                disabled={moduleComplete}
                id="cta-thanks-btn"
                onClick={handleContinue}
              />
            </div>
          </WizardCard>
          <WizardCard className={classes.captionCard} showBackButton={false}>
            <b>Still have questions?</b>
            <Typography variant="body2">
              For answers to more commonly asked questions, read the{' '}
              <a href="/compliance-guide">Compliance guide</a>
            </Typography>
          </WizardCard>
        </>
      ) : (
        <div className={desktop ? '' : classes.mobileMargin}>
          {desktop ? (
            <Grid container justifyContent="center" spacing={6}>
              <Grid container item justifyContent="flex-start" spacing={6} xs={11}>
                <Grid className={classes.previousPageButton} item>
                  <PreviousPageButton onBack={handleBack} />
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <PreviousPageButton onBack={handleBack} />
          )}

          <div className={classes.header}>
            <CardHeader gutterBottom={false}>
              You’re one step closer to helping protect {possessiverize(cardholderFirstName)}{' '}
              spending!
            </CardHeader>
            <CardText>
              Heads up: There are always two roles involved with a True Link Visa Card.
            </CardText>
          </div>
          <Grid container justifyContent="center" spacing={3}>
            <Grid item md={5}>
              <AdministratorCard />
            </Grid>
            <Grid item md={5}>
              <CardholderCard cardholderFirstName={cardholderFirstName} />
            </Grid>
          </Grid>
          <Box display="flex" justifyContent="center">
            <NextPageButton
              className={classes.nextPageButton}
              disabled={moduleComplete}
              id="cta-thanks-btn"
              onClick={handleContinue}
            />
          </Box>
        </div>
      )}
    </>
  );
}

Thanks.propTypes = {
  cardholderFirstName: PropTypes.string.isRequired,
  moduleComplete: PropTypes.bool.isRequired,
  setThanksData: PropTypes.func.isRequired,
  trusteeData: trusteeDataShape,
};
